import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, FileText } from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'

export const TERMS_LAST_UPDATED = 'May 2026'

function TH2({ children }) {
  return (
    <div className="mt-14 mb-5 pt-10 border-t border-gray-200">
      <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
        <span className="flex-shrink-0 w-1 h-7 rounded-full bg-gradient-to-b from-primary-500 to-accent-500" />
        {children}
      </h2>
    </div>
  )
}

function TItem({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-gray-600 leading-relaxed">
      <CheckCircle className="w-4 h-4 text-accent-500 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  )
}

function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/60 via-white to-white" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-accent-500/8 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex justify-center mb-5">
              <div className="flex items-center gap-2 badge-primary px-4 py-2">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-semibold">Terms of Service</span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Rules for Using{' '}
              <span className="gradient-text">Rithmo</span>
            </h1>

            <p className="text-gray-500 mb-6">Last updated: {TERMS_LAST_UPDATED}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 max-w-2xl mx-auto text-sm text-amber-900 leading-relaxed">
              By accessing or using Rithmo, you agree to these Terms of Service. If you disagree with any part,
              you may not use the Service.
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Document Body ─────────────────────────────────────────────────── */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.article
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >

            <section>
              <TH2>1. Agreement to Terms</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                By accessing or using Rithmo&apos;s AI meeting platform (&quot;Service&quot;), you agree to be bound by
                these Terms of Service. If you disagree with any part of these terms, you may not access the Service.
              </p>
            </section>

            <section>
              <TH2>2. Description of Service</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                Rithmo provides an AI-powered platform that integrates with video conferencing tools to help founders
                and teams run smarter meetings, surface participation insights, manage agendas, generate summaries
                and action items, and reclaim time through AI-assisted meeting operations.
              </p>
            </section>

            <section>
              <TH2>3. User Accounts</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                To use certain features of the Service, you must register for an account. You agree to:
              </p>
              <ul className="my-4 space-y-2">
                <TItem>Provide accurate and complete information</TItem>
                <TItem>Maintain the security of your account credentials</TItem>
                <TItem>Promptly notify us of any unauthorized access</TItem>
                <TItem>Accept responsibility for all activities under your account</TItem>
              </ul>
            </section>

            <section>
              <TH2>4. Acceptable Use</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">You agree not to use the Service to:</p>
              <ul className="my-4 space-y-2">
                <TItem>Violate any applicable laws or regulations</TItem>
                <TItem>Infringe upon the rights of others</TItem>
                <TItem>Record or monitor meetings without proper consent</TItem>
                <TItem>Attempt to gain unauthorized access to our systems</TItem>
                <TItem>Interfere with or disrupt the Service</TItem>
              </ul>
            </section>

            <section>
              <TH2>5. Meeting Data and Content</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                You retain ownership of any content you provide through the Service. By using our Service, you grant
                us a limited license to process your meeting data solely for the purpose of providing our features.
                We do not sell your data or use it to train AI models.
              </p>
            </section>

            <section>
              <TH2>6. Privacy</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                Your use of the Service is also governed by our{' '}
                <Link to="/privacy" className="text-primary-600 font-medium hover:underline">
                  Privacy Policy
                </Link>
                , which describes how we collect, use, and protect your information.
              </p>
            </section>

            <section>
              <TH2>7. Subscription and Payment</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                Some features of the Service require a paid subscription. By subscribing, you agree to pay the
                applicable fees. Subscriptions automatically renew unless cancelled before the renewal date.
              </p>
            </section>

            <section>
              <TH2>8. Intellectual Property</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                The Service and its original content, features, and functionality are owned by Rithmo and are
                protected by international copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section>
              <TH2>9. Disclaimer of Warranties</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                The Service is provided &quot;as is&quot; without warranties of any kind, either express or implied. We do
                not guarantee that the Service will be uninterrupted, secure, or error-free.
              </p>
            </section>

            <section>
              <TH2>10. Limitation of Liability</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                To the maximum extent permitted by law, Rithmo shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages resulting from your use of the Service.
              </p>
            </section>

            <section>
              <TH2>11. Changes to Terms</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of any changes by
                posting the new Terms on this page and updating the &quot;Last updated&quot; date.
              </p>
            </section>

            <section>
              <TH2>12. Termination</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                We may terminate or suspend your account and access to the Service immediately, without prior
                notice, for conduct that we believe violates these Terms or is harmful to other users, us, or
                third parties.
              </p>
            </section>

            <section>
              <TH2>13. Contact Us</TH2>
              <p className="mb-4 text-gray-600 leading-relaxed">
                If you have questions about these Terms, please contact us:
              </p>
              <ul className="my-4 space-y-3 text-gray-600">
                <li>
                  <span className="font-semibold text-gray-900">Email: </span>
                  <a href="mailto:legal@rithmo.ai" className="text-primary-600 font-medium hover:underline">
                    legal@rithmo.ai
                  </a>
                </li>
                <li>
                  <span className="font-semibold text-gray-900">Company: </span>
                  Rithmo, Inc.
                </li>
              </ul>
            </section>

          </motion.article>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default TermsPage
